/**
 * Keeps Product shop auto add-ons aligned with their trigger variants.
 *
 * Auto add-on lines are marked by the Product shop section with private
 * line-item properties. This makes the cleanup rule available globally,
 * including on /cart where the Product shop section is not rendered.
 */
(function () {
  'use strict';

  var MARKER = '_pcn_auto_addon';
  var TRIGGER_IDS = '_pcn_auto_addon_trigger_ids';
  var cleanupInFlight = false;
  var reconcileTimer;
  var hiddenLineKeys = {};

  function getSavedRules() {
    try {
      var rules = JSON.parse(window.localStorage.getItem('pcn-auto-addon-rules') || '{}');
      return Object.keys(rules).map(function (key) { return rules[key]; });
    } catch (e) {
      return [];
    }
  }

  function hasMatchingTrigger(cartVariantIds, triggerIds) {
    return triggerIds.some(function (id) {
      return cartVariantIds.indexOf(id) !== -1;
    });
  }

  function getSellingPlanId(item) {
    return item.selling_plan_allocation
      && item.selling_plan_allocation.selling_plan
      && String(item.selling_plan_allocation.selling_plan.id);
  }

  function isBonusItem(item) {
    var properties = item.properties || {};
    if (properties[MARKER] === 'true') return true;

    var sellingPlanId = getSellingPlanId(item);
    return getSavedRules().some(function (rule) {
      return String(item.variant_id || item.id) === String(rule.variantId)
        && sellingPlanId === String(rule.sellingPlanId);
    });
  }

  function applyHiddenBonusLines() {
    document.querySelectorAll('[id^="CartItem-"], [id^="CartDrawer-Item-"]').forEach(function (element) {
      var prefix = element.id.indexOf('CartDrawer-Item-') === 0
        ? 'CartDrawer-Item-'
        : 'CartItem-';
      var lineKey = element.id.slice(prefix.length);
      if (!hiddenLineKeys[lineKey]) return;
      element.style.display = 'none';
      element.setAttribute('aria-hidden', 'true');
    });
  }

  function hideBonusLines(cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    hiddenLineKeys = {};
    cart.items.forEach(function (item) {
      if (item.key && isBonusItem(item)) hiddenLineKeys[item.key] = true;
    });
    applyHiddenBonusLines();
  }

  function refreshCartDrawer() {
    try {
      document.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { open: false }
      }));
    } catch (e) {}
  }

  function reconcile(cart) {
    if (cleanupInFlight || !cart || !Array.isArray(cart.items)) return;

    var cartVariantIds = cart.items
      .filter(function (item) { return Number(item.quantity) > 0; })
      .map(function (item) { return String(item.variant_id || item.id); });

    var updates = {};
    cart.items.forEach(function (item) {
      var properties = item.properties || {};
      if (Number(item.quantity) <= 0 || !item.key) return;

      if (properties[MARKER] === 'true') {
        var triggerIds = String(properties[TRIGGER_IDS] || '')
          .split(',')
          .map(function (id) { return id.trim(); })
          .filter(Boolean);
        if (!hasMatchingTrigger(cartVariantIds, triggerIds)) updates[item.key] = 0;
        return;
      }

      // Compatibility for bonus lines added before private properties were
      // introduced. Match both variant and selling plan to avoid removing a
      // one-time purchase of the same variant.
      var sellingPlanId = getSellingPlanId(item);
      getSavedRules().some(function (rule) {
        var isMatchingBonus = String(item.variant_id || item.id) === String(rule.variantId)
          && sellingPlanId === String(rule.sellingPlanId);
        if (isMatchingBonus && !hasMatchingTrigger(cartVariantIds, rule.triggerIds || [])) {
          updates[item.key] = 0;
          return true;
        }
        return false;
      });
    });

    if (Object.keys(updates).length === 0) return;

    cleanupInFlight = true;
    fetch((window.theme && window.theme.routes && window.theme.routes.cart_update_url) || '/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ updates: updates })
    }).then(function (response) {
      return response.json().then(function (json) {
        if (!response.ok) throw json;
        return json;
      });
    }).then(function (updatedCart) {
      console.log('[pcn][auto-addon] removed orphaned bonus line(s)');
      if (window.theme && window.theme.pubsub) {
        window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          source: 'pcn-auto-addon-cleanup',
          cart: updatedCart
        });
      }
      refreshCartDrawer();
    }).catch(function (error) {
      console.warn('[pcn][auto-addon] global cleanup failed', error);
    }).finally(function () {
      cleanupInFlight = false;
    });
  }

  function getCartAndReconcile() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (response) {
        return response.json().then(function (cart) {
          if (!response.ok) throw cart;
          hideBonusLines(cart);
          reconcile(cart);
          return cart;
        });
      })
      .catch(function (error) {
        console.warn('[pcn][auto-addon] unable to read cart', error);
      });
  }

  function scheduleCartReconcile() {
    window.clearTimeout(reconcileTimer);
    reconcileTimer = window.setTimeout(function () {
      getCartAndReconcile();
    }, 150);
  }

  function observeCartMutations() {
    var originalFetch = window.fetch;
    if (!originalFetch || originalFetch._pcnAutoAddonObserved) return;

    function isCartMutation(input) {
      var url = typeof input === 'string' ? input : input && input.url;
      return typeof url === 'string' && /\/cart\/(?:change|update|clear)(?:\.js)?(?:[/?]|$)/.test(url);
    }

    function observedFetch() {
      var input = arguments[0];
      var request = originalFetch.apply(this, arguments);
      if (isCartMutation(input)) {
        request.then(function () {
          scheduleCartReconcile();
        }, function () {
          // A failed cart request cannot change the cart, so no reconciliation is needed.
        });
      }
      return request;
    }

    observedFetch._pcnAutoAddonObserved = true;
    window.fetch = observedFetch;
  }

  document.addEventListener('DOMContentLoaded', function () {
    observeCartMutations();
    getCartAndReconcile();

    if (window.theme && window.theme.pubsub) {
      window.theme.pubsub.subscribe(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (event && event.source === 'pcn-auto-addon-cleanup') return;
        scheduleCartReconcile();
      });
    }

    document.addEventListener('cart:refresh', function () {
      scheduleCartReconcile();
    });

    new MutationObserver(function () {
      applyHiddenBonusLines();
    }).observe(document.body, { childList: true, subtree: true });
  });
})();
